<template>
  <BaseModal :is-open="isVisible" @close="close">
    <div :class="['announcement', { 'announcement--with-image': hasImage }]">
      <div class="announcement__panel">
        <div class="announcement__text">
          <span v-if="tagLabel" :class="['announcement__tag', tagModifier]">
            {{ tagLabel }}
          </span>
          <h2 class="announcement__title">{{ currentPage.title }}</h2>
          <p v-sanitize-html="currentPage.description" class="large" />
        </div>

        <div class="announcement__actions">
          <BaseButton
            v-if="showSkipButton"
            outline
            size="large"
            data-cy="announcement-skip"
            @click="close"
          >
            {{ $t('buttons.SKIP') }}
          </BaseButton>

          <BaseButton
            v-if="showBackButton"
            outline
            size="large"
            data-cy="announcement-back"
            @click="pageIndex--"
          >
            {{ $t('buttons.BACK') }}
          </BaseButton>

          <BaseButton
            v-if="showNextButton"
            :theme="theme"
            outline
            size="large"
            data-cy="announcement-next"
            @click="pageIndex++"
          >
            {{ $t('buttons.NEXT') }}
          </BaseButton>

          <BaseButton
            v-if="showGotItButton"
            :theme="theme"
            size="large"
            data-cy="announcement-got-it"
            @click="close"
          >
            {{ $t('buttons.GOT_IT') }}
          </BaseButton>
        </div>
      </div>

      <div
        v-if="hasImage"
        :class="[
          'announcement__image-panel',
          `announcement__image-panel--${theme}`,
        ]"
      >
        <img
          v-if="currentPage.image"
          :key="pageIndex"
          :src="currentPage.image"
          alt=""
          class="announcement__image"
        />
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseButton from '@/components/generics/base/BaseButton.vue'
import BaseModal from '@/components/generics/base/BaseModal.vue'
import type {
  AnnouncementPage,
  AnnouncementTag,
  AnnouncementTheme,
} from '@/components/generics/modals/types'
import { useI18n } from '@/hooks/useI18N'
import { computed, onMounted, onUnmounted, ref } from 'vue'

type Props = {
  pages: AnnouncementPage[]
  tag?: AnnouncementTag
  theme?: AnnouncementTheme
  /** Milliseconds to wait before fading in, so the page has settled first. */
  delay?: number
}

const props = withDefaults(defineProps<Props>(), {
  tag: undefined,
  theme: 'blue',
  delay: 800,
})

const emit = defineEmits<{
  close: []
}>()

const i18n = useI18n()

const TAG_LABEL_KEYS: Record<AnnouncementTag, string> = {
  'new-feature': 'NEW_FEATURE',
  'feature-improvement': 'FEATURE_IMPROVEMENT',
}

const isVisible = ref(false)
const pageIndex = ref(0)

const currentPage = computed(() => props.pages[pageIndex.value])
const lastPageIndex = computed(() => props.pages.length - 1)
const hasManyPages = computed(() => props.pages.length > 1)
const hasImage = computed(() => props.pages.some((page) => page.image))

const tagLabel = computed(() =>
  props.tag ? i18n.t(TAG_LABEL_KEYS[props.tag]) : ''
)
const tagModifier = computed(() =>
  props.tag ? `announcement__tag--${props.tag}` : ''
)

const showSkipButton = computed(
  () => hasManyPages.value && pageIndex.value === 0
)
const showBackButton = computed(() => hasManyPages.value && pageIndex.value > 0)
const showNextButton = computed(
  () => hasManyPages.value && pageIndex.value !== lastPageIndex.value
)
const showGotItButton = computed(() => pageIndex.value === lastPageIndex.value)

let fadeInTimeout: ReturnType<typeof setTimeout>

onMounted(() => {
  fadeInTimeout = setTimeout(() => {
    isVisible.value = true
  }, props.delay)
})

onUnmounted(() => clearTimeout(fadeInTimeout))

const close = () => {
  isVisible.value = false
  emit('close')
}
</script>

<style scoped lang="scss">
.announcement {
  display: flex;
  width: 100%;
  max-width: 560px;

  &--with-image {
    justify-content: space-between;
    height: 100%;
    max-width: 1024px;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    padding: $spacing-xl;
  }

  // With an illustration alongside it the text sits in a fixed-height column,
  // bottom-aligned against the artwork; on its own it just wraps its content.
  &--with-image &__panel {
    width: 45%;
    height: 600px;
    padding: 30% $spacing-2xl $spacing-2xl;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: $spacing-4xs;
    color: $color-text;
  }

  &__title {
    @include subtitle;
    font-size: $font-size-3xl;
  }

  &__tag {
    @include micro-header;
    font-size: 11px;
    width: fit-content;
    margin-bottom: $spacing-4xs;
    padding: $spacing-5xs $spacing-4xs;
    border-radius: $border-radius-s;
    text-rendering: geometricPrecision;
    letter-spacing: 1.5px;
    font-weight: 900;
    animation: announcement-tag 500ms cubic-bezier(0.77, 0, 0.16, 1.435) forwards;
    animation-delay: 600ms;
    visibility: hidden;

    &--new-feature,
    &--feature-improvement {
      background: hsl(120, 100%, 85%);
      color: hsl(120, 100%, 15%);
    }
  }

  &__actions {
    margin-top: $spacing-m;
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2xs;
  }

  &__image-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 55%;

    &--red {
      background: linear-gradient($color-red-20, $color-red-40);
    }
    &--green {
      background: linear-gradient($color-green-20, $color-green-40);
    }
    &--blue {
      background: linear-gradient($color-blue-20, $color-blue-45);
    }
    &--purple {
      background: linear-gradient($color-purple-20, $color-purple-40);
    }
  }

  &__image {
    max-height: 300px;
    max-width: 85%;
    animation: announcement-image 400ms ease-in-out forwards;
    visibility: hidden;
  }
}

@media screen and (max-width: $small-desktop-width-max) {
  .announcement--with-image {
    flex-direction: column-reverse;

    .announcement__panel {
      width: 100%;
      height: 350px;
      padding: $spacing-l;
    }

    .announcement__image-panel {
      width: 100%;
    }

    .announcement__image {
      height: 356px;
      padding: $spacing-xl;
    }
  }
}

@keyframes announcement-tag {
  0% {
    transform: skewX(-8deg) scale(0);
    visibility: visible;
  }
  100% {
    transform: skewX(-8deg) scale(1);
    visibility: visible;
  }
}

@keyframes announcement-image {
  0% {
    visibility: visible;
    transform: translateY(100px);
    opacity: 0;
  }
  100% {
    visibility: visible;
    transform: translateY(0px);
    opacity: 1;
  }
}
</style>
